import { useState } from 'react';
import { PopoverButton } from './PopoverButton';
import { Checkbox } from './Checkbox';
import {
  axisFiltersActive,
  countAxisOption,
  defaultAxisFilterState,
  type AxisFilterState,
  type FilterAxis,
} from '../../domain/multiAxisFiltering';

export interface FilterButtonProps<T> {
  axes: FilterAxis<T>[];
  items: readonly T[];
  value: AxisFilterState;
  onChange: (next: AxisFilterState) => void;
  label?: string;
}

function activeAxisCount<T>(axes: FilterAxis<T>[], value: AxisFilterState): number {
  return axes.filter((axis) => {
    const axisValue = value[axis.id];
    return axis.type === 'multi'
      ? Array.isArray(axisValue) && axisValue.length > 0
      : typeof axisValue === 'string' && axisValue !== '';
  }).length;
}

/** Generic "Filtros" button: combinable multi-axis filters over any item list. */
export function FilterButton<T>({
  axes,
  items,
  value,
  onChange,
  label = 'Filtros',
}: FilterButtonProps<T>) {
  const [open, setOpen] = useState(false);

  const toggleMultiOption = (axisId: string, optionValue: string, checked: boolean) => {
    const current = (value[axisId] as string[] | undefined) ?? [];
    const next = checked ? [...current, optionValue] : current.filter((v) => v !== optionValue);
    onChange({ ...value, [axisId]: next });
  };

  const selectSingleOption = (axisId: string, optionValue: string) => {
    const current = value[axisId];
    onChange({ ...value, [axisId]: current === optionValue ? '' : optionValue });
  };

  return (
    <PopoverButton
      label={label}
      icon={<i className="ti ti-filter" aria-hidden="true" />}
      open={open}
      onOpenChange={setOpen}
      activeCount={activeAxisCount(axes, value)}
    >
      <div style={{ padding: 12, minWidth: 220 }}>
        {axes.map((axis) => (
          <div key={axis.id} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--z-muted)', marginBottom: 6 }}>
              {axis.label}
            </div>
            {axis.type === 'multi' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {axis.options.map((option) => (
                  <div
                    key={option.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Checkbox
                      label={option.label}
                      checked={((value[axis.id] as string[] | undefined) ?? []).includes(
                        option.value,
                      )}
                      onChange={(checked) => toggleMultiOption(axis.id, option.value, checked)}
                    />
                    <span style={{ fontSize: 11, color: 'var(--z-dim)' }}>
                      {countAxisOption(items, option)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {axis.options.map((option) => {
                  const active = value[axis.id] === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => selectSingleOption(axis.id, option.value)}
                      aria-pressed={active}
                      style={{
                        fontSize: 12,
                        padding: '4px 10px',
                        borderRadius: 999,
                        border: 'none',
                        cursor: 'pointer',
                        background: active ? 'var(--z-accent)' : 'var(--z-fill-2)',
                        color: active ? 'var(--z-on-accent)' : 'var(--z-muted)',
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 4,
            paddingTop: 10,
            borderTop: '1px solid var(--z-fill-2)',
          }}
        >
          <button
            type="button"
            onClick={() => onChange(defaultAxisFilterState(axes))}
            disabled={!axisFiltersActive(axes, value)}
            style={{
              fontSize: 12,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--z-muted)',
            }}
          >
            Limpiar
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            style={{
              fontSize: 12,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--z-accent)',
            }}
          >
            Listo
          </button>
        </div>
      </div>
    </PopoverButton>
  );
}
